import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { setPageMeta } from '@/modules/pageMeta';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
  }
}

const ContactView = () => import('@/views/ContactView.vue');
const TeamPartnerView = () => import('@/views/TeamPartnerView.vue');
const NewsView = () => import('@/views/NewsView.vue');
const JobsView = () => import('@/views/JobsView.vue');
const EventsView = () => import('@/views/EventsView.vue');
const ApproachTopicView = () => import('@/views/ApproachTopicView.vue');
const PracticeView = () => import('@/views/PracticeView.vue');
const HomeView = () => import('@/views/HomeView.vue');
const AboutView = () => import('@/views/AboutView.vue');
const LegalView = () => import('@/views/collections/LegalView.vue');
const BackgroundsView = () => import('@/views/BackgroundsView.vue');
const BackgroundsUseCasesView = () => import('@/views/BackgroundsUseCasesView.vue');
const BackgroundsGlView = () => import('@/views/BackgroundsGlView.vue');

const TeamDetailView = () => import('@/views/collections/TeamDetailView.vue');
const PartnerDetailView = () => import('@/views/collections/PartnerDetailView.vue');
const EventsDetailView = () => import('@/views/collections/EventsDetailView.vue');
const EventsCancelView = () => import('@/views/EventsCancelView.vue');
const NewsDetailView = () => import('@/views/collections/NewsDetailView.vue');


const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'HomeView',
    component: HomeView,
    meta: { title: 'iconomy' }
  },
  {
    path: '/about',
    name: 'AboutView',
    component: AboutView,
    meta: { title: 'iconomy — About' }
  },
  {
    path: '/tech-policy',
    name: 'TechPolicyView',
    component: PracticeView,
    meta: { title: 'iconomy — Tech Policy' },
    props: { practice: 'tech-policy' }
  },
  {
    path: '/venture-building',
    name: 'VentureBuildingView',
    component: PracticeView,
    meta: { title: 'iconomy — Venture Building' },
    props: { practice: 'venture-building' }
  },
  {
    path: '/community-building',
    name: 'CommunityBuildingView',
    component: PracticeView,
    meta: { title: 'iconomy — Community Building' },
    props: { practice: 'community-building' }
  },
  {
    path: '/approach/:topic',
    name: 'ApproachTopicView',
    component: ApproachTopicView,
    // fallback only — the view sets the per-topic title via setPageMeta
    meta: { title: 'iconomy approach' },
    props: {
      topic: {type: String, required: true}
    }
  },
  {
    path: '/events',
    name: 'EventsView',
    component: EventsView,
    meta: { title: 'iconomy events' }
  },
  {
      path: '/event/:url',
      name: 'EventDetailView',
      component: EventsDetailView,
      props: {
        url: {type:String, required: true}
      }
  },
  {
      path: '/event/cancel/:url',
      name: 'EventCancelView',
      component: EventsCancelView,
      props: {
        url: {type:String, required: true}
      }
  },
  {
    path: '/jobs',
    name: 'JobsView',
    component: JobsView,
    meta: { title: 'iconomy careers' }
  },
  {
    path: '/news',
    name: 'NewsView',
    component: NewsView,
    meta: { title: 'iconomy news' }
  },
  {
      path: '/news/:url',
      name: 'NewsDetailView',
      component: NewsDetailView,
      props: {
        url: {type:String, required: true}
      }
  },
  {
    path: '/team',
    name: 'TeamPartnerView',
    component: TeamPartnerView,
    meta: { title: 'iconomy team and partners' }
  },
  {
      path: '/team/:url',
      name: 'TeamDetailView',
      component: TeamDetailView,
      props: {
        url: {type:String, required: true}
      }
  },
  {
      path: '/partner/:url',
      name: 'PartnerDetailView',
      component: PartnerDetailView,
      props: {
        url: {type:String, required: true}
      }
  },
  {
    path: '/contact',
    name: 'ContactView',
    component: ContactView,
    meta: { title: 'iconomy contact' },
    props: {
      reason: {type:String, default: ""}
    }
  },
  {
    path: '/legal/:url',
    name: 'LegalView',
    component: LegalView,
    props: {
      url: {type:String, required: true}
    }
  },
  {
    // internal design reference, not linked in the navigation
    path: '/backgrounds',
    name: 'BackgroundsView',
    component: BackgroundsView,
    meta: { title: 'iconomy backgrounds' }
  },
  {
    path: '/backgrounds/use-cases',
    name: 'BackgroundsUseCasesView',
    component: BackgroundsUseCasesView,
    meta: { title: 'iconomy background use cases' }
  },
  {
    path: '/backgrounds/gl/:category',
    name: 'BackgroundsGlView',
    component: BackgroundsGlView,
    meta: { title: 'iconomy webgl backgrounds' },
    props: true
  },
  {
    path: '/imprint',
    redirect: '/legal/imprint',
  },
  {
    path: '/privacy',
    redirect: '/legal/privacy',
  },
  {
    path: '/:pathMatch(.*)',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }else{
      return { top: 0 }
    }
  }
})

router.afterEach((to) => {
  setPageMeta({ title: to.meta.title });
});

export default router
